import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import {
  Box,
  ButtonBase,
  ButtonBaseProps,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { memo, useEffect } from 'react';
import { bloxValues } from 'themes/constants';
import { getNavTextColor, navBorderRadiusPx } from '../../../../utility/styles';
import { PlusNavObj } from '../types';

type PlusItemBaseProps = Omit<
  ButtonBaseProps,
  | 'ref'
  | 'id'
  | 'sx'
  | 'children'
  | 'onClick'
  | 'component'
  | 'disableRipple'
  | 'aria-label'
>;

interface PlusNavItemProps extends PlusItemBaseProps {
  navItem: PlusNavObj;
  handleOnClick: (event: React.SyntheticEvent<HTMLElement>) => void;
  id?: string;
  sx?: SxProps<Theme>;
  className?: string;
  href?: string | undefined | null;
  hidden?: boolean;
  hideLabel?: boolean;
  polymorphAs?: any;
  disableRipple?: boolean;
  isDisabled?: boolean;
  asIcon?: boolean;
  handlePinToggle?: (item: PlusNavObj) => void;
  pinnedApps?: string[];
  hidePin?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  dndContainerRef?: React.RefObject<HTMLElement>;
}

const DnDBasePlusNavItem = memo(
  ({
    navItem,
    sx,
    handleOnClick,
    hideLabel,
    className,
    href,
    hidden,
    id,
    polymorphAs,
    disableRipple,
    isDisabled,
    asIcon = true,
    fullWidth = false,
    fullHeight = false,
    handlePinToggle,
    pinnedApps,
    hidePin,
    dndContainerRef,
    ...restProps
  }: PlusNavItemProps) => {
    const theme = useTheme();
    const { isDragging, attributes, listeners, setNodeRef, transform, transition } =
      useSortable({
        id: navItem.key,
      });

    const dndStyles: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0 : 1, // hide original as drop location spot.
    };

    const Icon = navItem?.icon;
    const navTextColor = getNavTextColor(theme);
    const isPinned = pinnedApps?.includes(navItem.key);
    const shouldDisable = Boolean(isDisabled || isDragging);
    const hrefPath = navItem.path || undefined;
    const isHidden = Boolean(hidden) ?? false;

    const handlePinClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (handlePinToggle && navItem?.key) {
        handlePinToggle(navItem);
      }
    };

    // Applies styles to the DnD container when dragging
    useEffect(() => {
      if (!dndContainerRef || !dndContainerRef.current) {
        return;
      }
      const dndBox = dndContainerRef.current;
      if (dndBox) {
        if (isDragging) {
          dndBox.style.outlineWidth = '2px';
          dndBox.style.outlineStyle = 'solid';
          dndBox.style.outlineColor = theme.palette.action.hover;
          dndBox.style.outlineOffset = '-1px';
          dndBox.style.backgroundColor = theme.palette.action.hover;
          dndBox.style.transition =
            'background-color 0.2s, outline 0.2s, outline-offset 0.2s';
        } else {
          dndBox.style.outlineWidth = '0px';
          dndBox.style.outlineStyle = 'solid';
          dndBox.style.outlineColor = 'transparent';
          dndBox.style.outlineOffset = '0';
          dndBox.style.backgroundColor = '';
          dndBox.style.transition =
            'background-color 0.2s, outline 0.2s, outline-offset 0.2s';
        }
      }
    }, [isDragging, theme, dndContainerRef]);

    return (
      <ButtonBase
        ref={setNodeRef} // dnd ref
        id={id}
        data-testid={id}
        onClick={handleOnClick}
        href={!shouldDisable && hrefPath ? hrefPath : undefined}
        component={polymorphAs}
        className={`${className ? className : ''}${
          shouldDisable ? ' itemdisabled' : ''
        }`.trim()}
        disableRipple={Boolean(disableRipple)}
        disabled={Boolean(shouldDisable)}
        hidden={isHidden}
        aria-hidden={isHidden}
        aria-label={`Create - ${navItem?.label || navItem?.path || 'unknown'}`}
        {...attributes}
        {...listeners}
        style={dndStyles} // Performance felt more fluid in styles prop vs spreading into sx props
        sx={{
          position: 'relative',
          display: isHidden ? 'none' : 'flex',
          flexDirection: asIcon ? 'column' : 'row',
          gap: 1,
          p: 1,
          width: fullWidth ? '100%' : 'auto',
          height: fullHeight ? '100%' : 'auto',
          borderRadius: navBorderRadiusPx,
          fontSize: bloxValues.regularFontSizeRem,
          color: shouldDisable ? theme.palette.action.disabled : navTextColor,
          bgcolor: 'transparent',
          '&:hover, &:focus-within': {
            color: !shouldDisable ? theme.palette.text.primary : navTextColor,
            bgcolor: theme.palette.action.hover,
          },
          '& .iconwrapper': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: navBorderRadiusPx,
            bgcolor: 'transparent',
            '& svg': {
              fontSize: asIcon ? 48 : 32,
            },
          },
          '& .itemicon': {
            display: 'flex',
          },
          '& .labelwrapper': {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: asIcon ? 'center' : 'flex-start',
            width: '100%',
            textAlign: asIcon ? 'center' : 'left',
          },
          '& .itemlabel': {
            fontSize: bloxValues.smallFontSizeRem,
            overflowWrap: 'anywhere',
            // Clamp long labels to two lines with an ellipsis so an arbitrary
            // title (e.g. a story template) can't blow out the tile height.
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textAlign: asIcon ? 'center' : 'left',
          },
          ...(sx ? (typeof sx === 'function' ? sx(theme) : sx) : {}),
        }}
        {...restProps}
      >
        <Tooltip
          title={isPinned ? 'Unpin' : 'Pin'}
          disableInteractive
          enterNextDelay={400}
        >
          <IconButton
            data-testid="plus-item-pin-button"
            component="span"
            onClick={handlePinClick}
            onMouseDown={(e) => {
              e.stopPropagation(); // Dont ripple the buttonbase on pin click
            }}
            size="small"
            sx={{
              transform: 'rotate(45deg)',
              display: hidePin ? 'none' : 'flex',
              position: asIcon ? 'absolute' : 'relative',
              top: asIcon ? -4 : 'unset',
              left: asIcon ? -7 : 'unset',
              zIndex: 2, // Ensure the pin button is on top
              '& svg': {
                fontSize: asIcon ? 18 : 24,
              },
            }}
          >
            {isPinned ? <PushPinRoundedIcon /> : <PushPinOutlinedIcon />}
          </IconButton>
        </Tooltip>

        {Icon ? (
          <Box
            id="plus-iconwrapper-id"
            data-testid="plus-iconwrapper-id"
            className="iconwrapper"
          >
            <Icon className="itemicon" />
          </Box>
        ) : null}

        <Box
          id="plus-labelwrapper-id"
          data-testid="plus-labelwrapper-id"
          className="labelwrapper"
        >
          {!hideLabel &&
            // Templates have arbitrary, possibly long titles that clamp to an
            // ellipsis, so surface the full title on hover. Apps have fixed short
            // labels and need no tooltip.
            (navItem?.template ? (
              <Tooltip
                title={navItem?.label}
                disableInteractive
                enterNextDelay={400}
              >
                <Typography
                  className="itemlabel"
                  id="plus-itemlabel-id"
                  data-testid="plus-itemlabel-id"
                >
                  {navItem?.label}
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                className="itemlabel"
                id="plus-itemlabel-id"
                data-testid="plus-itemlabel-id"
              >
                {navItem?.label}
              </Typography>
            ))}
        </Box>
      </ButtonBase>
    );
  },
);

DnDBasePlusNavItem.displayName = 'DnDBasePlusNavItem';
export default DnDBasePlusNavItem;
